import { useReducer, useState } from "react";
import { useApp } from "../../../../../context/AppContext";
import type { SalonService } from "../../../../../types/salon";
import type { GetTransactionsResponse } from "../../../../../types/transactions";
import { notifyError } from "../../../../../utils/notify";
import {
  BookingDraftState,
  bookingDraftReducer,
  initialBookingDraft,
  isDraftTouched,
} from "../../booking/bookingDraftReducer";
import { seedFromTransaction } from "../../booking/bookingSeed";
import {
  buildEditLineItemsPayload,
  resolveStaffServiceId,
} from "../../booking/rescheduleCommit";
import { SuggestedSlot } from "../../booking/useSuggestedTimes";
import { slotParts } from "./detailModel";

interface UseMobileRescheduleArgs {
  transaction: GetTransactionsResponse;
  /** Booked-service id of the tapped block (the row Start / Provider edit). */
  bookedServiceId: string;
  timezone: string;
  services: SalonService[];
  /** Write succeeded: the new day + transaction ids to refetch. */
  onSaved: (day: string, transactionIds: string[]) => void;
}

/**
 * The detail sheet's reschedule draft. Same model as the booking drawer's
 * edit mode: rows seeded by seedFromTransaction("edit"), changed through
 * bookingDraftReducer actions (the tapped service is the active row; Start /
 * Provider are `moveActiveToSlot`), the day owned by the draft (`dayChanged`,
 * single-day rule as the drawer). Save is the drawer's edit write
 * (buildEditLineItemsPayload → editTransactionLineItems), and the caller
 * refetches the source plus any cross-day split transactions.
 */
export function useMobileReschedule({
  transaction,
  bookedServiceId,
  timezone,
  services,
  onSaved,
}: UseMobileRescheduleArgs) {
  const { editTransactionLineItems } = useApp();

  const [seed] = useState(() =>
    seedFromTransaction(transaction, "edit", timezone, new Date()),
  );
  const [draft, dispatch] = useReducer(
    bookingDraftReducer,
    seed,
    (s): BookingDraftState => {
      const opened = bookingDraftReducer(initialBookingDraft, {
        type: "open",
        mode: "edit",
        origin: "calendar",
        sourceTxn: transaction,
        rows: s.rows,
        clientUuid: s.clientUuid,
        day: s.day,
      });
      const tapped = opened.rows.find(
        (r) => r.sourceBookedServiceId === bookedServiceId,
      );
      return tapped
        ? bookingDraftReducer(opened, {
            type: "activeChanged",
            uid: tapped.uid,
          })
        : opened;
    },
  );
  const [saving, setSaving] = useState(false);

  const { rows, day } = draft;
  const activeRow =
    rows.find((r) => r.uid === draft.activeUid) || rows[0] || null;

  const setDate = (next: string) => {
    if (next) dispatch({ type: "dayChanged", day: next });
  };
  const setTime = (time: string) => {
    if (time) dispatch({ type: "moveActiveToSlot", time, services });
  };
  const setProvider = (staffId: string) => {
    if (!activeRow) return;
    dispatch({
      type: "moveActiveToSlot",
      time: activeRow.time,
      staffId,
      services,
    });
  };
  const applySuggestion = (slot: SuggestedSlot) => {
    // Single tapped service: its start + provider (the row's own; the
    // endpoint honors staff_ids).
    const { day: slotDay, time } = slotParts(slot, timezone);
    dispatch({
      type: "moveActiveToSlot",
      time,
      day: slotDay,
      staffId: slot.services?.[0]?.staff_id || undefined,
      services,
    });
  };

  const save = async (): Promise<void> => {
    if (saving) return;
    if (!isDraftTouched(draft)) {
      onSaved(day, []);
      return;
    }
    setSaving(true);
    try {
      const payload = buildEditLineItemsPayload(
        transaction.transaction.id,
        rows,
        timezone,
        (r) => resolveStaffServiceId(services, r),
      );
      const edited = await editTransactionLineItems(payload);
      onSaved(day, [
        transaction.transaction.id,
        ...(edited?.split_transaction_uuids ?? []),
      ]);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to reschedule";
      notifyError(message, { title: "Could not reschedule" });
    } finally {
      setSaving(false);
    }
  };

  return {
    day,
    rows,
    activeRow,
    saving,
    setDate,
    setTime,
    setProvider,
    applySuggestion,
    save,
  };
}
